import { Component, OnInit, inject } from '@angular/core';
import { debounce } from 'lodash-es';
import { UserSettings } from 'app/core/enums/user-settings.enum';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  EncounterTypeEligibilityFilters,
  EncounterTypeEligibilityListSettings,
  EncounterTypeEligibilityUiOptions,
} from 'app/core/models/user-settings/encounter-type-eligibility-settings.model';
import { ClientEncounterTypeEligibility } from 'app/core/models/client-encounter-type-eligibility.model';
import { EncounterTypeEligibilityStatus } from 'app/core/enums/encounter-type-eligibility-status.enum';
import { EncounterTypeEligibilityStatusOptions } from 'app/core/options/encounter-type-eligibility-status.opts';
import { EncounterTypeEligibilitiesService } from 'app/core/services/encounter-type-eligibilities.service';
import { ClientEncounterTypeEligibilityTableComponent } from 'app/shared/components/client-encounter-type-eligibility-table/client-encounter-type-eligibility-table.component';
import { EncounterTypeEligibilitiesExplainerComponent } from 'app/shared/components/encounter-type-eligibilities-explainer/encounter-type-eligibilities-explainer.component';

@Component({
  selector: 'app-client-portal-encounter-type-eligibilities',
  templateUrl: './client-portal-encounter-type-eligibilities.component.html',
  styleUrls: ['./client-portal-encounter-type-eligibilities.component.scss'],
  imports: [
    ClientEncounterTypeEligibilityTableComponent,
    FormsModule,
    NgSelectComponent,
    EncounterTypeEligibilitiesExplainerComponent,
  ],
})
export class ClientPortalEncounterTypeEligibilitiesComponent implements OnInit {
  private encounterTypeEligibilitiesService = inject(EncounterTypeEligibilitiesService);
  private userSettingsService = inject(UserSettingsService);

  clientEncounterTypeEligibilities: ClientEncounterTypeEligibility[] = [];
  paging: LimitOffsetPaging = LimitOffsetPaging.empty;
  filters: EncounterTypeEligibilityFilters = new EncounterTypeEligibilityFilters();
  uiOptions: EncounterTypeEligibilityUiOptions = new EncounterTypeEligibilityUiOptions();
  loading = true;

  newClientEncounterTypeEligibilities: ClientEncounterTypeEligibility[] = [];
  newPaging: LimitOffsetPaging = LimitOffsetPaging.empty;
  newLoading = true;

  eligibilityStatusFilterOptions = EncounterTypeEligibilityStatusOptions.filter(
    ({ value }) => value !== EncounterTypeEligibilityStatus.new
  );

  ngOnInit() {
    this.loadSettings();
    this.loadEncounterTypeEligibilitySummaries();
    this.loadNewEncounterTypeEligibilitySummaries();
  }

  onFilterChange() {
    this.paging.currentPage = 1;
    this.saveSettings();
    this.loadEncounterTypeEligibilitySummaries();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onPageChange(_page: number) {
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onNewPageSizeChange(size: number) {
    this.newPaging.pageSize = size;
    this.loadNewEncounterTypeEligibilitySummaries();
  }

  onNewPageChange(_page: number) {
    this.loadNewEncounterTypeEligibilitySummaries();
  }

  onResetFiltersClick() {
    this.paging.currentPage = 1;
    this.filters = new EncounterTypeEligibilityFilters();
    this.loadEncounterTypeEligibilitySummaries();
    this.saveSettings();
  }

  onExplainerExpandOrCollapse(expanded: boolean) {
    this.uiOptions.explainerExpanded = expanded;
    this.saveSettings();
  }

  private loadEncounterTypeEligibilitySummaries() {
    this.loading = true;

    const filters = { ...this.filters };
    if (!filters.eligibilityStatuses?.length) {
      filters.eligibilityStatuses =
        [EncounterTypeEligibilityStatus.eligible, EncounterTypeEligibilityStatus.ineligible];
    }

    this.encounterTypeEligibilitiesService.getList(this.paging, filters).subscribe(result => {
      this.clientEncounterTypeEligibilities = result.clientEncounterTypeEligibilities;
      this.paging = result.meta.paging;
      this.loading = false;
    });
  }

  private loadSettings() {
    const listSettings: EncounterTypeEligibilityListSettings =
      this.userSettingsService.get<EncounterTypeEligibilityListSettings>(
        UserSettings.clientPortalEncounterTypeEligibilityListSettings
      );

    if (listSettings) {
      this.filters = listSettings.filters;
      this.paging = listSettings.paging;

      if (listSettings.uiOptions) {
        this.uiOptions = listSettings.uiOptions;
      }
    }
  }

  private saveSettings() {
    const listSettings = new EncounterTypeEligibilityListSettings();
    listSettings.filters = this.filters;
    listSettings.paging = this.paging;
    this.userSettingsService.save<EncounterTypeEligibilityListSettings>(
      UserSettings.clientPortalEncounterTypeEligibilityListSettings,
      listSettings
    );
  }

  private loadNewEncounterTypeEligibilitySummaries() {
    this.newLoading = true;
    const filters = new EncounterTypeEligibilityFilters();
    filters.eligibilityStatuses = [EncounterTypeEligibilityStatus.new];
    this.encounterTypeEligibilitiesService.getList(this.newPaging, filters).subscribe(result => {
      this.newClientEncounterTypeEligibilities = result.clientEncounterTypeEligibilities;
      this.newPaging = result.meta.paging;
      this.newLoading = false;
    });
  }

  onEncounterTypeKeyUp() {
    this.debounceFilterKeyUp();
  }

  private debounceFilterKeyUp = debounce(
    () => { this.onFilterChange(); },
    1000,
    { maxWait: 2000 }
  );
}
